import { Backdrop, Box, Tab } from "@mui/material";
import { CircularProgress, LinearProgress } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { TrackProps, TrackVersionsObj } from "../../pages/MyTracks/types";
import WaveSurfer from "wavesurfer.js";
import { MenuItemsContainer } from "../../pages/MyTracks/styles";
import { TrackContainer } from "../../pages/MyTracks/styles";
import { VersionsAndCollaboratorsContainer } from "../../pages/MyTracks/styles";
import { WaveformContainer, styles } from "../../pages/MyTracks/styles";
import { useNavigate } from "react-router-dom";
import { Spinner } from "reactstrap";
import { RpcError } from "grpc-web";
import { fetchTrack, postTrack } from "../../services/trackService";
import { GetTrackResponse } from "../../proto/api_pb";
import { MasterModal } from "../MasteringComponents/MasterModal";
import { SelectVersionModal } from "../TrackVersion/SelectVersionModal";
import { TrackVersionGroup } from "../TrackVersion/TrackVersionGroup";
import { TalkJsContext, TalkJsContextInterface } from "../../contexts";
import { ArtWork } from "./ArtWork/ArtWork";
import { TrackControls } from "./TrackControls/TrackControls";
import { TrackDetails } from "./TrackDetails/TrackDetails";
import { VersionDetails } from "./VersionDetails/VersionDetails";
import { TrackMenu } from "./TrackMenu/TrackMenu";
import { Collaborators } from "./TrackCollaborator/TrackCollaborators";
import { MenuItems } from "./MenuItem/MenuItems";
import { handleAddTrackVersion } from "./TrackHandler/trackHandlers";
import { handleCreateGroupChat } from "./TrackHandler/trackHandlers";
import { handleFetchTrackVersions } from "./TrackHandler/trackHandlers";
import { addDolbyEffect } from "./TrackHandler/trackHandlers";
import { addNewVersion, downloadSong } from "./TrackHandler/trackHandlers";
import { TrackFileGroup } from "../TrackFile/TrackFileGroup";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Slider from "@mui/material/Slider";
import { trackComponentStyles } from "./styles";
import { PublishPermissionModal } from "../Modal/PublishPermissionModal";
import Mixpanel from "../../utils/mixpanel";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { TrackPrivacySwitch } from "./TrackPrivacySwitch/TrackPrivacySwitch";
import { APP_COLOR } from "../../theme";

const TrackComponent: React.FC<TrackProps> = ({
  item,
  setAudioTrack,
  handlePlay,
  handleStop,
  handleOpen,
  setLoading,
  setSelectedVersion,
  handleState,
  state,
  handlePreviewState,
  previewState,
  openDeleteConfirmModal,
  expand,
  handleOpenVersionModal,
  userVerified,
  handleOpenCollabsModal,
  handleFetch,
  handleOpenTrackFileModal,
  playingTime,
  setPlayingTime,
  audioDuration,
  singleTrackPage,
  stopAll,
  isFileAdded,
  isTrackConnectedToTutorial,
  setEvokeCallBack,
  evokeCallBack,
  hasPaymentMethod,
}) => {
  const { getValidAccessToken } = useGetValidAccessToken();
  const { track, trackUrl, isPlaying, waveSurfer } = item;
  const ref = useRef<HTMLDivElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [selectedTrackVersionLabel, setSelectedTrackVersionLabel] = useState<
    string | undefined
  >("");
  const [versions, setVersions] = useState<TrackVersionsObj[]>();
  const [versionBpm, setVersionBpm] = useState<number | undefined>(undefined);
  const [isMasterModalOpen, setIsMasterModalOpen] = useState<boolean>(false);
  const [masteredMusicUrl, setMasteredMusicUrl] = useState<string | undefined>(
    undefined
  );
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [selectedPreset, setSelectedPreset] = useState("Bubbly");
  const [jobId] = useState<string | undefined>(undefined);
  const [previewUrl, setPreviewUrl] = useState<Map<string, string> | undefined>(
    undefined
  );
  const [isSelectVersionModalOpen, setIsSelectVersionModalOpen] =
    useState<boolean>(false);
  const [changeVersionLoading, setChangeVersionLoading] =
    useState<boolean>(false);
  const [waveSurferAudioTrack, setWaveSurferAudioTrack] = useState<
    WaveSurfer | undefined
  >();
  const [hover, setHover] = useState<boolean>(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const navigate = useNavigate();
  const image = track?.getImageUrl();
  const id = track?.getId();
  const title = track?.getTitle();
  const artist = track?.getArtist();
  const genres = track?.getSongMetadata()?.getGenre();
  const contributors = track?.getCollaboratorsList();
  const handleMasterModalOpen = () => {
    setEvokeCallBack && setEvokeCallBack((prev) => prev + 1);
    handleStop(id!);
    setIsMasterModalOpen(true);
  };
  const handleMasterModalClose = () => setIsMasterModalOpen(false);
  const talkJsContext = useContext<TalkJsContextInterface>(TalkJsContext);
  const [createGroupChatClicked, setCreateGroupChatClicked] = useState(false);
  const [tabValue, setTabValue] = useState("versions");
  const [isTrackPublished, setIsTrackPublished] = useState(false);

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const createGroupChat = (
    id: string | undefined,
    title: string | undefined
  ) => {
    handleCreateGroupChat(
      talkJsContext,
      contributors,
      navigate,
      setCreateGroupChatClicked,
      id,
      title
    );
  };

  useEffect(() => {
    if (isMasterModalOpen && stopAll) stopAll();
    if (createGroupChatClicked && talkJsContext) {
      talkJsContext.createConversation(id!, `${title}`);
    }
  }, [
    talkJsContext,
    createGroupChatClicked,
    id,
    title,
    isMasterModalOpen,
    stopAll,
  ]);

  const handleUseEffect = useCallback(async () => {
    const token = await getValidAccessToken();
    const response = await handleFetchTrackVersions(token, id!);
    setVersions(response);
  }, [getValidAccessToken, id]);

  useEffect(() => {
    handleUseEffect();
  }, [handleUseEffect]);

  useEffect(() => {
    if (ref.current && !waveSurfer) {
      const audioTrack = WaveSurfer.create({
        container: ref.current,
        responsive: true,
        cursorWidth: 0,
        barWidth: 4,
        barRadius: 5,
        progressColor: APP_COLOR,
      });
      setWaveSurferAudioTrack(audioTrack);
      if (singleTrackPage && singleTrackPage === true) {
        trackUrl !== undefined && audioTrack.load(trackUrl);
        audioTrack.empty();
        trackUrl !== undefined && audioTrack.load(trackUrl);

        audioTrack.on("ready", function () {
          setLoaded(true);
        });
      }
      setAudioTrack(id!, audioTrack);
    }
  }, [trackUrl, id, setAudioTrack, waveSurfer, singleTrackPage]);

  useEffect(() => {
    const findOutput = versions?.find(
      (el) => item.track?.getTrackVersionId() === el.track?.getId()
    );
    if (findOutput) {
      if (setSelectedVersion) setSelectedVersion(findOutput);
      setSelectedTrackVersionLabel(findOutput.track?.getLabel());
      setVersionBpm(findOutput.track?.getAudioMetadata()!.getBpm());
    }
    return () => {
      waveSurfer?.stop();
    };
  }, [waveSurfer, item.track, versions, setSelectedVersion]);

  // TODO: instead of a full page load try and reload just this cards data
  useEffect(() => {
    const handleFetchTrack = async () => {
      if (track?.getTrackVersionId().length === 0) {
        const token = await getValidAccessToken();
        try {
          const response = (await fetchTrack(token, id!)) as GetTrackResponse;
          if (response.getTrack()!.getTrackVersionId().length > 0) {
            response.toObject();
          }
        } catch (e) {
          const error = e as RpcError;
          console.log(error.code);
          console.log(error.message);
        }
      }
    };
    handleFetchTrack();
  }, [getValidAccessToken, id, track]);

  const hoverOn = () => {
    if (!expand) {
      setHover(true);
    }
  };

  const hoverOff = () => {
    if (!expand) {
      setHover(false);
    }
  };

  const handleChange = async (timeStamp: number) => {
    if (setLoading) setLoading(true);
    let version;
    if (versions) {
      version = versions.find((el) => el.track?.getTimestamp() === timeStamp);
    }
    if (track && version && version.track) {
      setSelectedTrackVersionLabel(version?.track?.getLabel());
      track.setTrackVersionId(version?.track?.getId());
      const postTrackResponse = await postTrack(
        track,
        await getValidAccessToken()
      );
      if (postTrackResponse instanceof RpcError) {
        if (setLoading) setLoading(false);

        throw postTrackResponse;
      }
    }
    if (setLoading) setLoading(false);

    if (waveSurferAudioTrack && ref.current) {
      waveSurferAudioTrack.empty();
    }
    if (handleFetch) handleFetch();
    const div = document.getElementsByClassName("waveformRef")[0];
    div.innerHTML = "";
    waveSurferAudioTrack?.load(trackUrl);
    const token = await getValidAccessToken();
    const response = await handleFetchTrackVersions(token, id!);
    setVersions(response);
  };

  const createNewVersion = async () => {
    const token = await getValidAccessToken();
    if (setLoading) setLoading(true);
    const response = await addNewVersion(
      track,
      masteredMusicUrl,
      selectedPreset,
      jobId,
      previewUrl?.get(selectedPreset),
      token,
      id!
    );
    setVersions(response);
    if (setLoading) setLoading(false);
    setIsMasterModalOpen(false);
  };

  const processTrackVersion = async (
    sliderValue: number,
    selectedPresetValue: string,
    trackUrl: string,
    trackVersionId: string
  ) => {
    if (setLoading) setLoading(true);
    const token = await getValidAccessToken();
    await handleAddTrackVersion(
      selectedPresetValue,
      sliderValue,
      token,
      trackUrl,
      trackVersionId,
      setMasteredMusicUrl,
      setPreviewLoading,
      handleState
    );
    if (setLoading) setLoading(false);
  };

  const timeString = (timeStamp: number) => {
    const minutes = Math.floor(timeStamp / 60);
    const seconds = Math.ceil(timeStamp % 60);

    const formattedTime =
      seconds >= 10 ? minutes + ":" + seconds : minutes + ":0" + seconds;

    return formattedTime;
  };

  const getDolbyEffect = async (
    selectedPresetArray: string[],
    sliderValue: number,
    trackUrl: string,
    trackVersionId: string,
    previewStartValue: number | undefined,
    duration: number | undefined
  ) => {
    const accessToken = await getValidAccessToken();
    addDolbyEffect(
      selectedPresetArray,
      sliderValue,
      accessToken,
      trackUrl,
      trackVersionId,
      setPreviewUrl,
      setPreviewLoading,
      handlePreviewState,
      previewStartValue,
      duration
    );
  };

  const handleSliderChange = (event: any, newValue: any) => {
    if (setPlayingTime) setPlayingTime(newValue);
    waveSurferAudioTrack?.setCurrentTime(newValue);
  };

  const makeRefEmpty = () => {
    if (waveSurferAudioTrack && ref.current) {
      waveSurferAudioTrack.empty();
      const div = document.getElementsByClassName("waveformRef")[0];
      if (div) div.innerHTML = "";
    }
  };

  return (
    <>
      {isPublishModalOpen && (
        <PublishPermissionModal
          track={track}
          open={isPublishModalOpen}
          setIsPublishModalOpen={setIsPublishModalOpen}
          handleFetch={handleFetch}
          makeRefEmpty={makeRefEmpty}
        />
      )}

      {changeVersionLoading && (
        <Backdrop
          open={changeVersionLoading}
          invisible={false}
          sx={{
            zIndex: 1500,
          }}
        >
          <Box sx={trackComponentStyles.circularProgressContainer}>
            <CircularProgress color="inherit" />
            <h5 style={{ marginTop: "15px" }}>Selecting version ...</h5>
          </Box>
        </Backdrop>
      )}
      {isSelectVersionModalOpen && (
        <SelectVersionModal
          isSelectVersionModalOpen={isSelectVersionModalOpen}
          setIsSelectVersionModalOpen={setIsSelectVersionModalOpen}
          versions={versions}
          setChangeVersionLoading={setChangeVersionLoading}
          handleChange={handleChange}
          selectedTrackVersionLabel={selectedTrackVersionLabel}
        />
      )}

      {isMasterModalOpen && (
        <MasterModal
          isMasterModalOpen={isMasterModalOpen}
          handleMasterModalOpen={handleMasterModalOpen}
          handleMasterModalClose={handleMasterModalClose}
          track={item}
          addDolbyEffect={getDolbyEffect}
          masteredMusicUrl={masteredMusicUrl}
          handleAddTrackVersion={processTrackVersion}
          state={state}
          previewState={previewState}
          previewLoading={previewLoading}
          setPreviewLoading={setPreviewLoading}
          previewUrl={previewUrl}
          addNewVersion={createNewVersion}
          hasPaymentMethod={hasPaymentMethod}
          trackUrl={trackUrl}
          trackVersionId={track!.getTrackVersionId()}
          setSelectedPreset={setSelectedPreset}
        />
      )}
      <Box
        id={isTrackConnectedToTutorial ? "step-21" : ""}
        sx={{
          ...trackComponentStyles.trackComponentMainContainer,
          ...{
            boxShadow: hover
              ? "0px 10px 20px 2px rgba(0,0,0,0.75)"
              : "0px 5px 10px 0px rgba(0,0,0,0.7)",
            transform: hover ? `translate(0,-5px)` : `translate(0,0)`,
          },
        }}
        onMouseEnter={hoverOn}
        onMouseLeave={hoverOff}
      >
        <Box sx={{ width: "calc(100%)", height: "auto" }}>
          {
            <MenuItemsContainer>
              <TrackPrivacySwitch
                isTrackPublished={isTrackPublished}
                isTrackConnectedToTutorial={isTrackConnectedToTutorial}
                setIsPublishModalOpen={setIsPublishModalOpen}
                track={track}
                setIsTrackPublished={setIsTrackPublished}
              />
              <MenuItems
                expand={expand}
                handleOpen={handleOpen}
                handleOpenVersionModal={handleOpenVersionModal}
                handleOpenTrackFileModal={handleOpenTrackFileModal}
                loaded={loaded}
                downloadSong={() => downloadSong(trackUrl, title)}
                track={track}
                id={id}
                setIsMasterModalOpen={setIsMasterModalOpen}
                isMasterModalOpen={isMasterModalOpen}
                openDeleteConfirmModal={openDeleteConfirmModal}
                trackUrl={trackUrl}
                userVerified={userVerified}
              />
            </MenuItemsContainer>
          }
          <TrackContainer
            sx={{ zIndex: -2 }}
            onClick={() => {
              if (!expand) {
                Mixpanel.track("track_row_clicked");
                navigate(`/track/${id}`, { state: { id: id } });
              }
            }}
          >
            <ArtWork expand={expand} image={image} />
            <Box sx={{ width: "100%", paddingTop: "10px" }}>
              <Box sx={styles.trackControlsAndTrackDetailsContainer}>
                <Box sx={{ display: "flex", marginTop: "10px" }}>
                  <TrackControls
                    loaded={loaded}
                    handlePlay={handlePlay}
                    isPlaying={isPlaying}
                    id={id}
                    handleStop={handleStop}
                  />
                  <TrackDetails title={title} artist={artist} />
                </Box>
                <TrackMenu
                  handleOpenVersionModal={handleOpenVersionModal}
                  handleOpenTrackFileModal={handleOpenTrackFileModal}
                  id={id}
                  downloadSong={() => downloadSong(trackUrl, title)}
                  handleOpen={handleOpen}
                  setIsMasterModalOpen={setIsMasterModalOpen}
                  isMasterModalOpen={isMasterModalOpen}
                  userVerified={userVerified}
                  track={track}
                  trackUrl={trackUrl}
                  openDeleteConfirmModal={openDeleteConfirmModal}
                  expand={expand}
                  setIsPublishModalOpen={setIsPublishModalOpen}
                  isTrackPublished={isTrackPublished}
                  setIsTrackPublished={setIsTrackPublished}
                  hasPaymentMethod={hasPaymentMethod}
                  handleMasterModalOpen={handleMasterModalOpen}
                  isTrackConnectedToTutorial={isTrackConnectedToTutorial}
                />
              </Box>
              <Box sx={{ width: "100%", padding: "0 25px 0 25px" }}>
                {!expand && (
                  <Box
                    style={!expand ? { display: "none" } : {}}
                    ref={ref}
                  ></Box>
                )}
                {expand && (
                  <Box>
                    {track!.getTrackVersionId().length > 0 && expand ? (
                      <WaveformContainer>
                        <Box>
                          <Box
                            className="waveformRef"
                            style={styles.waveform}
                            ref={ref}
                          >
                            {!loaded && (
                              <Spinner style={styles.spinner} color="info" />
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ width: "100%", display: "flex" }}>
                          <Box
                            sx={{
                              ...trackComponentStyles.timeSpanContainer,
                              ...{ marginRight: "15px" },
                            }}
                          >
                            {playingTime !== 0
                              ? timeString(playingTime)
                              : "00:00"}
                          </Box>
                          <Slider
                            value={Math.round(playingTime)}
                            aria-label="Default"
                            valueLabelDisplay="auto"
                            onChange={handleSliderChange}
                            min={0}
                            max={audioDuration}
                          />
                          <Box sx={trackComponentStyles.timeSpanContainer}>
                            {audioDuration !== 0
                              ? timeString(audioDuration)
                              : "00:00"}
                          </Box>
                        </Box>
                      </WaveformContainer>
                    ) : (
                      <Box sx={styles.linearProgress}>
                        <LinearProgress color="inherit" />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              <VersionsAndCollaboratorsContainer>
                <VersionDetails
                  selectedTrackVersionLabel={selectedTrackVersionLabel}
                  versions={versions}
                />
                <Collaborators
                  genres={genres}
                  bpm={versionBpm}
                  expand={expand}
                  contributors={contributors}
                  handleOpenCollabsModal={handleOpenCollabsModal}
                  id={id}
                  loaded={loaded}
                  handleCreateGroupChat={createGroupChat}
                  trackTitle={track?.getTitle()}
                  isTrackConnectedToTutorial={isTrackConnectedToTutorial}
                />
              </VersionsAndCollaboratorsContainer>
            </Box>
          </TrackContainer>
        </Box>
        <Box>
          {expand && (
            <Box sx={{ width: "100%" }}>
              <TabContext value={tabValue}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <TabList onChange={handleChangeTab}>
                    <Tab
                      sx={trackComponentStyles.tab}
                      label="Versions"
                      value="versions"
                    />
                    <Tab
                      sx={trackComponentStyles.tab}
                      label="Files"
                      value="files"
                    />
                  </TabList>
                </Box>
                <TabPanel value="versions">
                  <TrackVersionGroup
                    id={id}
                    selectedVersionId={track?.getTrackVersionId()}
                    handleChange={handleChange}
                    isPlaying={isPlaying}
                    handleStop={handleStop}
                    evokeCallBack={evokeCallBack!}
                  />
                </TabPanel>
                <TabPanel value="files">
                  <TrackFileGroup id={id} isFileAdded={isFileAdded} />
                </TabPanel>
              </TabContext>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default TrackComponent;
